import React from "react";
import { Dropdown, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import { Link } from "react-router-dom";

import {
  PROJECT_PRIORITY_OPTIONS,
  STATUS_CATEGORY_COLORS,
  PROJECT_VISIBILITY_OPTIONS,
} from "../../constants/projects.constants";
import { all_routes } from "../../../router/all_routes";
import CustomPagination from "../../../../core/common/customPagination/CustomPagination";
import type { Project, ProjectFilters } from "../../../../core/data/redux/projects/types";

interface ProjectTableProps {
  projects: Project[];
  loading: boolean;
  filters: ProjectFilters;
  totalCount: number;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
  onFilterChange: (partial: Partial<ProjectFilters>) => void;
}

const formatDate = (value: string | null | undefined) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

const priorityLabel = (priority: number | null | undefined) =>
  PROJECT_PRIORITY_OPTIONS.find((o) => o.value === priority)?.label || "—";

const PRIORITY_COLORS: Record<number, string> = {
  0: "blue",
  1: "orange",
  2: "red",
};

const visibilityLabel = (visibility: number | null | undefined) =>
  PROJECT_VISIBILITY_OPTIONS.find((o) => o.value === visibility)?.label || "Public";

const VISIBILITY_COLORS: Record<number, string> = {
  0: "green",
  1: "purple",
  2: "volcano",
};

const ProjectTable: React.FC<ProjectTableProps> = ({
  projects,
  loading,
  filters,
  totalCount,
  onEdit,
  onDelete,
  onFilterChange,
}) => {
  const getActionItems = (project: Project): MenuProps["items"] => [
    {
      key: "open",
      icon: <i className="ti ti-external-link" style={{ color: "#3b82f6" }} />,
      label: (
        <Link to={all_routes.projectOverview.replace(":projectKey", project.key)}>
          Open project
        </Link>
      ),
    },
    {
      key: "edit",
      icon: <i className="ti ti-edit" style={{ color: "#f59e0b" }} />,
      label: "Edit",
      onClick: () => onEdit(project),
    },
    { type: "divider" },
    {
      key: "delete",
      icon: <i className="ti ti-trash" />,
      danger: true,
      label: "Delete",
      onClick: () => onDelete(project),
    },
  ];

  const columns: ColumnsType<Project> = [
    {
      title: "SrNo.",
      key: "srNo",
      width: 70,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {((filters.page || 1) - 1) * (filters.limit || 50) + index + 1}
        </span>
      ),
    },
    {
      title: <Tooltip title="Project Key & Name">Project</Tooltip>,
      dataIndex: "name",
      key: "name",
      width: 260,
      render: (name, project) => (
        <Tooltip
          title={
            <div style={{ padding: 4 }}>
              <div className="fw-bold pb-1 mb-1 border-bottom border-secondary">{name}</div>
              <div style={{ fontSize: 12 }}>
                <i className="ti ti-hash me-1" />
                {project.key}
              </div>
              {project.description && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {project.description.replace(/<[^>]*>/g, "").slice(0, 100)}
                </div>
              )}
            </div>
          }
          color="#1e293b"
          placement="right"
        >
          <div className="d-flex flex-column">
            <h6
              className="fw-medium mb-1 text-truncate"
              style={{ maxWidth: 240, fontSize: 13.5, color: "#0f172a" }}
            >
              <Link
                to={all_routes.projectOverview.replace(":projectKey", project.key)}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                {name}
              </Link>
            </h6>
            <span className="text-muted text-truncate" style={{ fontSize: 12, maxWidth: 240 }}>
              <i className="ti ti-hash me-1" />
              {project.key}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="Project Priority">Priority</Tooltip>,
      dataIndex: "priority",
      key: "priority",
      width: 110,
      render: (p) => (
        <Tooltip title={`Priority: ${priorityLabel(p)}`}>
          <Tag color={PRIORITY_COLORS[p] || "default"} style={{ fontSize: 11.5, margin: 0 }}>
            {priorityLabel(p)}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="Issue Completion Progress">Progress</Tooltip>,
      key: "progress",
      width: 200,
      render: (_, project) => {
        const stats = project.stats || {
          total: 0,
          by_status_category: { TODO: 0, IN_PROGRESS: 0, DONE: 0 },
        };
        const total = stats.total || 0;
        const done = stats.by_status_category?.DONE || 0;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        return (
          <Tooltip title={`${done} of ${total} done (${pct}%)`}>
            <div className="d-flex align-items-center gap-2">
              <div
                className="progress flex-grow-1"
                style={{ height: 6, background: "#e5e7eb", borderRadius: 4 }}
              >
                <div
                  className="progress-bar"
                  style={{
                    width: `${pct}%`,
                    background: STATUS_CATEGORY_COLORS.DONE,
                    transition: "width 0.5s ease",
                  }}
                />
              </div>
              <span className="small text-muted" style={{ minWidth: 30 }}>
                {pct}%
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Project Status">Status</Tooltip>,
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status) => (
        <Tooltip title={status === 1 ? "Active" : "Inactive"}>
          <Tag
            color={status === 1 ? "green" : "red"}
            style={{ fontSize: 11.5, margin: 0, textTransform: "capitalize" }}
          >
            {status === 1 ? "Active" : "Inactive"}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="Project Visibility">Visibility</Tooltip>,
      dataIndex: "visibility",
      key: "visibility",
      width: 100,
      render: (v) => {
        const val = v ?? 0;
        return (
          <Tooltip title={`Visibility: ${visibilityLabel(val)}`}>
            <Tag color={VISIBILITY_COLORS[val] || "default"} style={{ fontSize: 11.5, margin: 0 }}>
              {visibilityLabel(val)}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Start Date">Start</Tooltip>,
      dataIndex: "start_date",
      key: "start_date",
      width: 120,
      render: (d) => (
        <Tooltip title={formatDate(d)}>
          <span className="text-muted" style={{ fontSize: 12.5 }}>
            <i className="ti ti-calendar me-1" />
            {formatDate(d)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="End Date">End</Tooltip>,
      dataIndex: "end_date",
      key: "end_date",
      width: 120,
      render: (d) => (
        <Tooltip title={formatDate(d)}>
          <span className="text-muted" style={{ fontSize: 12.5 }}>
            <i className="ti ti-calendar me-1" />
            {formatDate(d)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, project) => (
        <Dropdown
          menu={{ items: getActionItems(project) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <button
            type="button"
            className="btn btn-icon btn-sm btn-light"
            style={{ borderRadius: 6 }}
            onClick={(e) => e.stopPropagation()}
          >
            <i className="ti ti-dots-vertical" />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="card-body p-0 mb-3 border-0">
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12 }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            />
            <p className="mt-3 text-muted">Loading projects...</p>
          </div>
        </div>
      ) : projects.length > 0 ? (
        <div
          className="bg-white"
          style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}
        >
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={projects}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: 1100 }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
            }
          />
          <CustomPagination
            currentPage={filters.page || 1}
            totalPages={Math.ceil(totalCount / (filters.limit || 50))}
            total={totalCount}
            limit={filters.limit || 50}
            hasNextPage={(filters.page || 1) * (filters.limit || 50) < totalCount}
            hasPrevPage={(filters.page || 1) > 1}
            onPageChange={(page) => onFilterChange({ page })}
            onLimitChange={(limit) => onFilterChange({ limit, page: 1 })}
          />
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12 }}
        >
          <div className="text-center">
            <i className="ti ti-folder-off" style={{ fontSize: 48, color: "#cbd5e1" }} />
            <h5 className="mt-3 mb-2 fw-semibold">No Projects Found</h5>
            <p className="text-muted mb-0">
              Try adjusting your filters or create a new project.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectTable;
